/**
 * Where to land after any successful authentication.
 *
 * Every sign-in path used to hardcode `/apps`. The CLI authorization pages need
 * the user back where they started, and there are five such paths (password,
 * passkey step-up, TOTP, recovery code, passwordless), so the decision lives in
 * one place rather than five.
 */

export const DEFAULT_POST_LOGIN_PATH = "/apps";

/**
 * Only same-origin paths are honored. `//evil.example` is a protocol-relative
 * URL that a browser resolves off-site, so a single leading slash is not enough
 * on its own.
 */
export function isInternalPath(value: unknown): value is string {
  return typeof value === "string" && isInternalPathShape(stripUrlWhitespace(value));
}

/**
 * URL parsing removes ASCII tab, newline and carriage return before it parses,
 * so a tab after the leading slash still reads as a protocol-relative URL. The
 * guard has to compare the same string a parser would see.
 */
function stripUrlWhitespace(value: string): string {
  return value.replace(/[\t\n\r]/g, "");
}

function isInternalPathShape(value: string): boolean {
  return value.startsWith("/") && !value.startsWith("//") && !value.startsWith("/\\");
}

export function resolvePostLoginPath(redirect: unknown): string {
  // Returns the stripped form, so the string that was checked is the string
  // that gets used.
  return isInternalPath(redirect) ? stripUrlWhitespace(redirect) : DEFAULT_POST_LOGIN_PATH;
}

const UNAUTHENTICATED_AUTH_PATHS = new Set([
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify",
  "/verify-email",
]);

function pathnameOf(path: string): string {
  const cut = path.search(/[?#]/);
  const raw = cut === -1 ? path : path.slice(0, cut);
  return raw.length > 1 && raw.endsWith("/") ? raw.slice(0, -1) : raw;
}

export function resolveLoginBouncePath(redirect: unknown): string {
  const dest = resolvePostLoginPath(redirect);
  return UNAUTHENTICATED_AUTH_PATHS.has(pathnameOf(dest)) ? DEFAULT_POST_LOGIN_PATH : dest;
}
